import type {
  DateRangeObject,
  OutputKeyObject,
  WithBlankSearchObject,
  WithExclusionObject,
} from '@ps-smart-cp/ppfw-client';

import type { ItemAttributes, ItemNames } from '@ps-smart-cp/ppfw-client';
import {
  Characters,
  DateRange,
  ItemName,
  Locale,
  Numeric,
  OutputKey,
  SingleDate,
  SingleValue,
  WithBlankSearch,
  WithExclusion,
} from '@ps-smart-cp/ppfw-client';

interface Schema {
  code: string;
  name: string;
  mstSpareName01: string;
  mstSpareName02: string;
  validityStartDate: Date | null;
  validityEndDate: Date | null;
  mstNote: string;
  codeRange: WithExclusionObject;
  mstSpareName01Condition: WithBlankSearchObject;
  mstSpareName02Condition: WithBlankSearchObject;
  validityStartDateCondition: DateRangeObject;
  validityEndDateCondition: DateRangeObject;
  mstNoteCondition: WithBlankSearchObject;
  outputKey: OutputKeyObject;
  /**
   * 変更後名称
   */
  displayName: string;
  /**
   * 項目文字数
   */
  codeDigits: string;
  /**
   * 基本名称
   */
  basicName: string;
}

interface Record {
  code: string;
  name: string;
  mstSpareName01: string;
  mstSpareName02: string;
  validityStartDate: string;
  validityEndDate: string;
  mstNote: string;
  codeRange: string;
  isCodeRangeExcluded: boolean;
  isMstSpareName01BlankOnly: boolean;
  isMstSpareName02BlankOnly: boolean;
  validityStartDateFrom: string;
  validityStartDateTo: string;
  validityEndDateFrom: string;
  validityEndDateTo: string;
  isMstNoteBlankOnly: boolean;
  displayName: string;
  codeDigits: string;
  basicName: string;
}

const attributes = {
  code: Characters.preset.halfWidth({ minLen: 3, maxLen: 3, required: true }),
  name: Characters.preset.freeFormat({ required: true }),
  mstSpareName01: Characters.preset.freeFormat(),
  mstSpareName02: Characters.preset.freeFormat(),
  validityStartDate: SingleDate.ymd(true),
  validityEndDate: SingleDate.ymd(true),
  mstNote: Characters.preset.freeFormat(),
  codeRange: WithExclusion.of(Characters.preset.halfWidth({ maxLen: 250 })),
  mstSpareName01Condition: WithBlankSearch.of(Characters.preset.freeFormat()),
  mstSpareName02Condition: WithBlankSearch.of(Characters.preset.freeFormat()),
  validityStartDateCondition: DateRange.ymd(),
  validityEndDateCondition: DateRange.ymd(),
  mstNoteCondition: WithBlankSearch.of(Characters.preset.freeFormat()),
  outputKey: OutputKey.of(SingleValue.shortValue(), SingleValue.longValue()),
  displayName: Characters.preset.freeFormat({ required: true }),
  codeDigits: Numeric.of(1, 999, 0, true),
  basicName: Characters.preset.freeFormat(),
} satisfies ItemAttributes<Schema>;

const names = {
  code: ItemName.of([Locale.ja, 'コード'], [Locale.en, 'Code']),
  name: ItemName.of([Locale.ja, '名称'], [Locale.en, 'Name']),
  mstSpareName01: ItemName.of([Locale.ja, '予備名称1'], [Locale.en, 'Spare Name One']),
  mstSpareName02: ItemName.of([Locale.ja, '予備名称2'], [Locale.en, 'Spare Name Two']),
  validityStartDate: ItemName.of([Locale.ja, '有効開始年月日'], [Locale.en, 'Validity Start Date']),
  validityEndDate: ItemName.of([Locale.ja, '有効終了年月日'], [Locale.en, 'Validity End Date']),
  mstNote: ItemName.of([Locale.ja, 'マスタ備考'], [Locale.en, 'Mst Note']),
  codeRange: ItemName.of([Locale.ja, 'コード範囲'], [Locale.en, 'Code Range']),
  mstSpareName01Condition: ItemName.of([Locale.ja, '予備名称1'], [Locale.en, 'Spare Name One']),
  mstSpareName02Condition: ItemName.of([Locale.ja, '予備名称2'], [Locale.en, 'Spare Name Two']),
  validityStartDateCondition: ItemName.of([Locale.ja, '有効開始年月日'], [Locale.en, 'Validity Start Date']),
  validityEndDateCondition: ItemName.of([Locale.ja, '有効終了年月日'], [Locale.en, 'Validity End Date']),
  mstNoteCondition: ItemName.of([Locale.ja, 'マスタ備考'], [Locale.en, 'Mst Note']),
  outputKey: ItemName.of([Locale.ja, 'キー'], [Locale.en, 'Key']),
  displayName: ItemName.of([Locale.ja, '変更後名称'], [Locale.en, 'Display Name']),
  codeDigits: ItemName.of([Locale.ja, '項目文字数'], [Locale.en, 'Code Digits']),
  basicName: ItemName.of([Locale.ja, '基本名称'], [Locale.en, 'Basic Name']),
} satisfies ItemNames<Schema>;

const titleNames = {
  registration: ItemName.of([Locale.ja, 'マスタ登録']),
  maintenance: ItemName.of([Locale.ja, 'マスタメンテナンス']),
  import: ItemName.of([Locale.ja, 'マスタ取込']),
  modify: ItemName.of([Locale.ja, 'マスタ変更']),
  search: ItemName.of([Locale.ja, 'マスタ検索']),
  setting: ItemName.of([Locale.ja, 'マスタ設定-マスタ情報設定']),
};

const mstCommon = {
  titleNames,
  names,
  attributes,
};

interface MstCommon {
  schema: Schema;
  record: Record;
}

export { mstCommon };
export type { MstCommon };
